import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/router'
import commerce from '@lib/api/commerce'
import { Layout } from '@components/common'
import { ProductView } from '@components/product'
import {
  getProductWithHandle,
  getGlobalContent,
  getUpselling
} from '@lib/sanity.queries'
import TRANSLATIONS from '@locales/index'
import ShopifyGidSlug from '@models/ShopifyGidSlug';
import dbConnect from '@lib/dbConnect'


export async function getStaticProps({
  params,
  locale = 'de',
  locales,
  preview,
}: GetStaticPropsContext<{ slug: string }>) {
  const config = { locale, locales }
  const global = await getGlobalContent({ lang: locale }, preview)
  const upselling = await getUpselling({ lang: locale }, preview)
  const productPromise = commerce.getProduct({
    variables: { slug: params!.slug },
    config,
    preview,
  })
  const { product } = await productPromise

  if (!product) {
    console.error(`Product with slug '${params?.slug}' not found`)
    return {
      notFound: true
    }
  }

  const productData = await getProductWithHandle({ lang: locale, handle: params?.slug }, preview);

  const sellingPlanSlugs = productData?.subscriptionConfig?.plans?.map((plan: any) => plan?.sellingPlanSlug) ?? [];
  if (sellingPlanSlugs.length > 0) {
    await dbConnect();

    const sellingPlans = await ShopifyGidSlug.find({
      type: 'sellingPlan',
      slug: {
        $in: sellingPlanSlugs
      }
    });
    let sellingPlanMap = new Map<string, string>();
    sellingPlans?.forEach(plan => sellingPlanMap.set(plan.slug, plan.gid));

    for (const plan of productData?.subscriptionConfig?.plans) {
      plan.sellingPlanId = sellingPlanMap.get(plan.sellingPlanSlug) ?? null;
    }
  }

  return {
    props: {
      product,
      productData,
      global,
      upselling,
      showPromo: true,
      messages: TRANSLATIONS[locale]
    },
    revalidate: 600,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const { products } = await commerce.getAllProductPaths()
  const productsWithPages = products.filter((product: any) => product.hasProductPage?.value === 'true');

  return {
    paths: locales
      ? locales.reduce<string[]>((arr, locale) => {
          // Add a product path for every locale
          productsWithPages.forEach((product: any) => {
            arr.push(`/${locale}/products${product.path}`)
          })
          return arr
        }, [])
      : productsWithPages.map((product: any) => `/products${product.path}`),
    fallback: 'blocking',
  }
}

export default function Slug({
  product,
  productData,
  messages
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()
  return router.isFallback ? (
    <h1>Loading...</h1>
  ) : (
    <ProductView product={product} productData={productData} messages={messages} />
  )
}

Slug.Layout = Layout
